import { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { UserRole } from '../../types';
import Loading from './Loading';

interface ProtectedRouteProps {
  children: ReactNode;
  allowedRoles?: UserRole[];
}

export function ProtectedRoute({ children, allowedRoles }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();

  // Show loading while checking authentication
  if (isLoading) {
    return <Loading fullScreen message="Checking authentication..." />;
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check role-based access
  // SUPER_ADMIN always has access to everything
  if (allowedRoles && user) {
    const userRoles = user.roles || (user.role ? [user.role] : []);
    const hasAccess = userRoles.includes(UserRole.SUPER_ADMIN) ||
                     userRoles.some(role => allowedRoles.includes(role));

    if (!hasAccess) {
      // Redirect employees to their portal
      if (userRoles.includes(UserRole.EMPLOYEE)) {
        return <Navigate to="/employee-portal" replace />;
      }
      // Redirect HR to employees page
      if (userRoles.includes(UserRole.HR)) {
        return <Navigate to="/employees" replace />;
      }
      return <Navigate to="/" replace />;
    }
  }

  return <>{children}</>;
}

export default ProtectedRoute;
